import { Menu, Switch, Divider, Dropdown, notification, Button, ConfigProvider } from "antd";
import { useState, useEffect } from "react";
import {
  LogoutOutlined,
  SunOutlined,
  MoonOutlined,
  HomeOutlined,
  MedicineBoxOutlined,
  OrderedListOutlined,
  TransactionOutlined,
  WifiOutlined,
  DownOutlined,
  SnippetsOutlined,
  LoginOutlined,
  DollarCircleOutlined,
  PhoneOutlined,
  UserAddOutlined,
  GlobalOutlined,
  SettingOutlined,
  ClusterOutlined,
  UsergroupAddOutlined,
  UserOutlined,
  UserSwitchOutlined,
  SearchOutlined,
  FileTextOutlined,
  PullRequestOutlined,
  SwapOutlined,
  LineChartOutlined,
  FileOutlined,
  KeyOutlined,
} from "@ant-design/icons";
import { useTheme } from "../../store/context/theme-context";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";

import { useTranslation } from "react-i18next";
import { logoutUser } from "../../store/redux/actions/userActions";
import LanguageSwitcher from "../../shared/languages/LanguageSwitcher";
import { useLanguage } from "../../store/context/language-context";
import { languageMap } from "../../shared/languages/LanguageSelector";
import {
  useDashboardLayout,
  DASHBOARD_LAYOUTS,
} from "../../store/context/dashboard-layout-context";
import { getSessionData } from "../../utils/interceptors/localStorageService";
import {
  featuresEnum,
  PermissionsEnum,
  PERMISSION_CODES,
  SelfUserClassificationEnum,
  FeatureCodeEnum,
} from "../../shared/constants/constants";
import { useFeatures } from "../../store/context/feature-context";
import { navigateToAuth } from "../../utils/helper/redirect";
import { usePermissions } from "../../store/context/permission-context";
import { darkenHex, hexToRgba } from "../../utils/helper/colorUtils";
import { useDashboard } from "../../store/context/DashboardContext";
import { useTenant } from "../../store/context/tenant-context";

// Customer nav items that require a core banking account. When a user only has a
// profile (anonymous loan applicant, hasCoreBankingAccounts === false) these are
// shown but disabled — only Dashboard and My Loans stay clickable.
const ACCOUNT_GATED_MENU_KEYS = new Set([
  "AccountStatement",
  "Transactions",
  "Transfer",
  "AirtimeRecharge",
  "DataRecharge",
  "BillsPayment",
  "savings",
  "merchantProfile",
  "Request",
]);

const Sidebar = ({ collapsed, type }) => {
  const { theme, toggleTheme } = useTheme();
  const tenant = useTenant();
  const { layout } = useDashboardLayout();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { findFeature, hasFeature } = useFeatures();
  const { findPermission } = usePermissions();
  const { bankingProfile } = useDashboard();
  // Profile-only customers (no core banking account yet) can browse but not transact.
  const requiresAccount = bankingProfile?.hasCoreBankingAccounts === false;
  // console.log("permission from context-sidebar", permissions);
  const { language: languageCode, showModal } = useLanguage();
  const [sessionData, setSessionData] = useState(null);
  const [initials, setInitials] = useState("");

  const addFeatureMenuItem = (featureKeys, item, permissionKey) => {
    // If featureKeys is an array, check all features
    const checkFeatures = () => {
      if (Array.isArray(featureKeys)) {
        return featureKeys.every((key) => findFeature(key));
      }
      return findFeature(featureKeys);
    };

    const shouldRender = permissionKey
      ? checkFeatures() && findPermission(permissionKey)
      : checkFeatures();

    return shouldRender ? [item] : [];
  };

  const addFeatureMenuItemByCode = (featureCodes, item, permissionKey) => {
    const checkFeatures = () => {
      if (Array.isArray(featureCodes)) {
        return featureCodes.every((code) => hasFeature(code));
      }
      return hasFeature(featureCodes);
    };

    const shouldRender = permissionKey
      ? checkFeatures() && findPermission(permissionKey)
      : checkFeatures();

    return shouldRender ? [item] : [];
  };

  useEffect(() => {
    const storedSessionData = getSessionData();
    if (storedSessionData) {
      setSessionData(storedSessionData);
      const firstNameInitial = (storedSessionData?.firstName ?? "")
        .charAt(0)
        .toUpperCase();
      const lastNameInitial = (storedSessionData?.lastName ?? "")
        .charAt(0)
        .toUpperCase();
      setInitials(`${firstNameInitial}${lastNameInitial}`);
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "logoutEvent") {
        navigateToAuth({ isAdmin: false });

        window.location.reload();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [navigate]);

  const handleSignIn = () => {
    if (hasFeature(FeatureCodeEnum.CUSTOMER_PORTAL))
      navigateToAuth({ isAdmin: false });
    else {
      //show a message to show that this feature is not available
      notification.info({ message: "Sign In feature not available." });
    }
  };

  const handleRegister = () => {
    if (hasFeature(FeatureCodeEnum.CUSTOMER_PORTAL))
      navigate("/authentication?mode=signup");
    // if(findFeature(featuresEnum.ADMIN_PORTAL))
    else {
      //navigate("/authentication/admin/register");
      //show a message to show that this feature is not available
      notification.info({ message: "Registration feature not available." });
    }
  };

  const handleSignOut = () => {
    dispatch(logoutUser());
    sessionData?.userClassification ===
    SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
      ? navigateToAuth({ isAdmin: true })
      : navigateToAuth({ isAdmin: false });
  };

  const handleMenuClick = ({ key }) => {
    const item = items.find((item) => item.key === key); // Find the clicked item
    if (item) {
      if (item.path) {
        navigate(item.path); // Navigate to the path if it exists
      } else if (item.onClick) {
        item.onClick(); // Call the onClick handler if it exists
      }
    }
  };

  const ProfileDropdown = () => {
    const profileMenuItems = [
      {
        key: "profile",
        icon: <UserOutlined />,
        label: "My Profile",
      },
      // Removed Settings from dropdown - use main sidebar Settings menu instead
      // {
      //   key: "settings",
      //   icon: <SettingOutlined />,
      //   label: "Settings",
      // },
      // {
      //   key: "divider",
      //   type: "divider",
      // },
      // {
      //   key: "logout",
      //   icon: <LogoutOutlined />,
      //   label: "Logout",
      //   onClick: handleSignOut,
      // },
    ];

    const handleMenuClick = ({ key }) => {
      switch (key) {
        case "profile":
          navigate("/settings/profile");
          break;
        default:
          return;
      }
    };
    return (
      <ConfigProvider
        theme={isDarkLayout ? {
          components: {
            Menu: {
              colorBgContainer: layout === DASHBOARD_LAYOUTS.GLASSMORPHISM ? "#1a2332" : "#0d3d2b",
              itemColor: "rgba(255,255,255,0.75)",
              itemHoverColor: "#fff",
              itemHoverBg: "rgba(255,255,255,0.08)",
              itemSelectedColor: "#fff",
              itemSelectedBg: "rgba(255,255,255,0.12)",
            },
          },
        } : {}}
      >
        <Dropdown
          menu={{
            items: profileMenuItems,
            onClick: handleMenuClick,
            selectable: false,
          }}
          trigger={["click"]}
          placement="bottomRight"
          overlayStyle={{
            marginTop: "8px",
            ...(isDarkLayout && {
              background: layout === DASHBOARD_LAYOUTS.GLASSMORPHISM ? "#1a2332" : (tenant?.theme?.colorPrimary || "#0b3d2e"),
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
            }),
          }}
        >
          <DownOutlined
            style={{ color: isDarkLayout ? "rgba(255,255,255,0.6)" : tenant.theme.colorPrimary }}
            className="cursor-pointer pointer-events-auto"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          />
        </Dropdown>
      </ConfigProvider>
    );
  };

  const profileName = `${sessionData?.displayName}` || "";
  // const accountType =
  //   sessionData?.accountType === 1
  //     ? "Personal"
  //     : sessionData?.accountType === 2 || sessionData?.accountType === 0
  //       ? "Corporate"
  //       : null;

  const languageName = languageMap[languageCode];

  const isDarkLayout =
    layout === DASHBOARD_LAYOUTS.MODERN_GRADIENT ||
    layout === DASHBOARD_LAYOUTS.GLASSMORPHISM;

  const items = [
    {
      key: collapsed ? "collapsed" : "expanded",
      style: { height: "auto" },
      className: "non-interactive",
      label: (
        <div className="flex items-center gap-2 py-4 px-1">
          {!collapsed && (
            <>
              <img
                src={theme === "dark" ? tenant.darkLogo : tenant.logo}
                alt={tenant?.companyName}
                style={{
                  width: 38,
                  height: 38,
                  objectFit: "contain",
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 15,
                  fontWeight: 700,
                  color: isDarkLayout ? "#fff" : "inherit",
                  lineHeight: 1.2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {tenant?.companyName}
              </span>
            </>
          )}
        </div>
      ),
    },
    ...(type === "portal"
      ? [
          {
            key: "divider1",
            width: "auto",
            label: <Divider />,
            className: "non-interactive",
          },
          {
            key: "home",
            icon: <HomeOutlined />,
            label: t("Home"),
            path: "/",
          },
          {
            key: "signin",
            icon: <LoginOutlined />,
            label: "Sign In",
            onClick: handleSignIn,
          },
          {
            key: "register",
            icon: <UserAddOutlined />,
            label: "Register",
            onClick: handleRegister,
          },
          ...addFeatureMenuItem(featuresEnum.PORTAL_CALL_BACK_REQUEST, {
            key: "confirm-details",
            icon: <PhoneOutlined />,
            label: t("Request a CallBack"),
            path: "/confirm-details",
          }),
          {
            key: "divider2",
            width: "auto",
            label: <Divider />,
            className: "non-interactive",
          },
          ...addFeatureMenuItem(featuresEnum.PORTAL_LANGUAGE_TOGGLE, {
            key: "english",
            icon: <GlobalOutlined onClick={showModal} />,
            label: (
              <div>
                <p
                  type="text"
                  onClick={showModal}
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {languageName}
                </p>
                <LanguageSwitcher />
              </div>
            ),
          }),
          ...addFeatureMenuItem(featuresEnum.PORTAL_DARK_MODE_SWITCH, {
            key: "theme",
            icon:
              theme === "dark" ? (
                <MoonOutlined onClick={toggleTheme} />
              ) : (
                <SunOutlined onClick={toggleTheme} />
              ),
            label: (
              <div className="flex justify-between items-center">
                <span className="mr-2">Dark Mode</span>
                <Switch
                  checked={theme === "dark"}
                  onChange={toggleTheme}
                  size="small"
                  disabled
                />
              </div>
            ),
          }),
          {
            key: "userswitch",
            icon:
              theme === "dark" ? (
                <UserSwitchOutlined />
              ) : (
                // the best ant design icon to use for switching admin is
                <UserSwitchOutlined />
              ),
            label: (
              <div className="flex justify-between items-center">
                <span className="mr-2">Admin Switch</span>
                <Switch
                  // checked={theme === "dark"}
                  onChange={() => navigateToAuth({ isAdmin: true })}
                  size="small"
                />
              </div>
            ),
          },
        ]
      : []),
    ...(type === "inner"
      ? [
          {
            key: "profile",
            className: "non-interactive",
            style: { height: "auto" },
            label: !collapsed && (
              <div
                style={{
                  background: isDarkLayout
                    ? "rgba(255,255,255,0.08)"
                    : theme === "dark"
                    ? "#1d1d1d"
                    : "#f5f5f5",
                  border: isDarkLayout
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "none",
                }}
                className="flex items-center justify-start px-1 py-3 pr-3 rounded-lg my-3 mx-0 gap-3"
              >
                <div
                  className="flex justify-center items-center text-lg text-white rounded-full select-none size-11 min-w-11"
                  style={{ backgroundColor: tenant.theme.colorPrimary }}
                >
                  {initials || "PN"}
                </div>
                <div className="flex-1 h-12 text-base">
                  <p>
                    <span
                      style={{ color: isDarkLayout ? "#fff" : "inherit" }}
                      className="block text-sm font-medium capitalize"
                    >
                      {profileName}
                    </span>
                    <span
                      style={{
                        color: isDarkLayout
                          ? "rgba(255,255,255,0.5)"
                          : theme === "dark"
                          ? "#8c8c8c"
                          : "#595959",
                      }}
                      className="text-sm block"
                    >
                      {sessionData?.userClassificationDescription}
                    </span>
                  </p>
                </div>
                {sessionData?.userClassification !==
                  SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS
                    .value && <ProfileDropdown />}
              </div>
            ),
          },
          ...(sessionData?.userClassification ===
          SelfUserClassificationEnum.BACKOFFICE_ADMINISTRATORS.value
            ? [
                findPermission(PermissionsEnum.ADM_DASH) && {
                  key: "adminDashboard",
                  icon: <HomeOutlined />,
                  label: t("Dashboard"),
                  path: "/admin-dashboard",
                },
                // findPermission(PermissionsEnum.ADM_SEARCH) &&
                ...addFeatureMenuItem(
                  featuresEnum.ADMIN_GENERAL_SEARCH,
                  {
                    key: "AdminGeneralSearch",
                    icon: <SearchOutlined />,
                    label: t("General Search"),
                    path: "/general-search",
                  },
                  PermissionsEnum.ADM_SEARCH,
                ),
                ...addFeatureMenuItem(
                  featuresEnum.ADMIN_CUSTOMERS_REPORT,
                  {
                    key: "AdminCustomers",
                    icon: <UsergroupAddOutlined />,
                    label: t("Customers"),
                    path: "/customers-report",
                  },
                  PERMISSION_CODES.CHANNEL_CUSTOMERS.VIEW_CUSTOMERS,
                ),
                ...addFeatureMenuItem(
                  featuresEnum.ADMIN_LOAN_REQUESTS,
                  {
                    key: "AdminLoanRequests",
                    icon: <FileTextOutlined />,
                    label: t("Loan Requests"),
                    path: "/loan-requests-report",
                  },
                  PERMISSION_CODES.CHANNEL_LOAN_REQUESTS.VIEW_LOAN_REQUESTS,
                ),
                ...addFeatureMenuItem(
                  featuresEnum.ADMIN_TRANSACTIONS_REPORT,
                  {
                    key: "AdminTransactions",
                    icon: <SnippetsOutlined />,
                    label: t("Transactions"),
                    path: "/transactions-report",
                  },
                  PERMISSION_CODES.CHANNEL_TRANSACTIONS.VIEW_TRANSACTIONS,
                ),
                ...addFeatureMenuItem(
                  featuresEnum.ADMIN_TRANSFER_REQUESTS,
                  {
                    key: "AdminTransfers",
                    icon: <TransactionOutlined />,
                    label: t("Transfers"),
                    path: "/transfers-report",
                  },
                  PERMISSION_CODES.CHANNEL_TRANSFERS.VIEW_TRANSFERS,
                ),
                ...addFeatureMenuItem(
                  [
                    featuresEnum.ADMIN_NIBSS_MANDATE_SETUP,
                    featuresEnum.ADMIN_NIBSS_MANDATE_COLLECTION,
                  ],
                  {
                    key: "MandateManagment",
                    icon: <FileTextOutlined />,
                    label: t("Mandate Management"),
                    path: "/mandate/selector",
                  },
                ),
                ...addFeatureMenuItem(featuresEnum.ADMIN_MANAGE_CHANNELS, {
                  key: "AdminGeneralRequests",
                  icon: <OrderedListOutlined />,
                  label: t("Manage Channels"),
                  path: "/general-requests-selector",
                }),
                ...addFeatureMenuItem(featuresEnum.ADMIN_ORGANISATION_SETUP, {
                  key: "AdminOrganisations",
                  icon: <ClusterOutlined />,
                  label: t("Organisations"),
                  path: "/organisations",
                }),
                ...addFeatureMenuItem(
                  featuresEnum.ADMIN_PRODUCT_CONFIGURATION,
                  {
                    key: "AdminProductConfiguration",
                    icon: <SettingOutlined />,
                    label: t("Product Configuration"),
                    path: "/product-configuration",
                  },
                ),
                {
                  key: "AdminProcessInstance",
                  icon: <SettingOutlined />,
                  label: t("Process Instance"),
                  path: "/ProcessInstance",
                },
              ]
            : [
                {
                  key: "dashboard",
                  icon: <HomeOutlined />,
                  label: t("Dashboard"),
                  path: "/dashboard",
                },
                ...addFeatureMenuItemByCode(
                  FeatureCodeEnum.CUSTOMER_ACCOUNT_STATEMENT,
                  {
                    key: "AccountStatement",
                    icon: <FileTextOutlined />,
                    label: t("Account Statement"),
                    path: "/account-statement",
                  },
                ),
                ...addFeatureMenuItemByCode(
                  FeatureCodeEnum.CUSTOMER_TRANSACTIONS,
                  {
                    key: "Transactions",
                    icon: <TransactionOutlined />,
                    label: t("Transactions"),
                    path: "/my-transactions",
                  },
                ),
                ...addFeatureMenuItemByCode(FeatureCodeEnum.CUSTOMER_TRANSFER, {
                  key: "Transfer",
                  icon: <SwapOutlined />,
                  label: t("Transfer"),
                  path: "/transfer",
                }),
                ...addFeatureMenuItemByCode(
                  FeatureCodeEnum.CUSTOMER_AIRTIME_RECHARGE,
                  {
                    key: "AirtimeRecharge",
                    icon: <PhoneOutlined />,
                    label: t("Airtime Recharge"),
                    path: "/airtime",
                  },
                ),
                ...addFeatureMenuItemByCode(
                  FeatureCodeEnum.CUSTOMER_DATA_PURCHASE,
                  {
                    key: "DataRecharge",
                    icon: <WifiOutlined />,
                    label: t("Data Recharge"),
                    path: "/data",
                  },
                ),
                ...addFeatureMenuItemByCode(FeatureCodeEnum.CUSTOMER_BILL, {
                  key: "BillsPayment",
                  icon: <MedicineBoxOutlined />,
                  label: t("Bills Payment"),
                  path: "/bills-payment",
                }),
                ...addFeatureMenuItemByCode(FeatureCodeEnum.CUSTOMER_LOAN, {
                  key: "loans",
                  icon: <DollarCircleOutlined />,
                  label: t("My Loans"),
                  path: "/loans",
                }),
                ...addFeatureMenuItemByCode(FeatureCodeEnum.CUSTOMER_SAVINGS, {
                  key: "savings",
                  icon: <LineChartOutlined />,
                  label: t("My Savings"),
                  path: "/my-savings",
                }),
                ...addFeatureMenuItemByCode(
                  FeatureCodeEnum.CUSTOMER_SUBACCOUNTS,
                  {
                    key: "merchantProfile",
                    icon: <FileOutlined />,
                    label: t("Subaccounts"),
                    path: "/merchant-profile",
                  },
                ),

                {
                  key: "Request",
                  icon: <PullRequestOutlined />,
                  label: t("Requests"),
                  path: "/requests",
                },
                // Ultimate Agent hidden — not ready for release
              ]),
          {
            key: "divider3",
            width: "auto",
            label: <Divider />,
            className: "non-interactive",
          },
          {
            key: "settings",
            icon: <SettingOutlined />,
            label: t("Settings & More"),
            path: "/settings",
          },
          ...addFeatureMenuItem(featuresEnum.PORTAL_LANGUAGE_TOGGLE, {
            key: "english",
            icon: <GlobalOutlined />,
            label: (
              <div>
                <p
                  type="text"
                  onClick={showModal}
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  {languageName}
                </p>
                <LanguageSwitcher />
              </div>
            ),
          }),
          ...addFeatureMenuItem(featuresEnum.PORTAL_DARK_MODE_SWITCH, {
            key: "theme",
            icon:
              theme === "dark" ? (
                <MoonOutlined onClick={toggleTheme} />
              ) : (
                <SunOutlined onClick={toggleTheme} />
              ),
            label: (
              <div className="flex justify-between items-center">
                <span className="mr-2">Dark Mode</span>
                <Switch
                  checked={theme === "dark"}
                  onChange={toggleTheme}
                  size="small"
                  disabled
                />
              </div>
            ),
          }),

          {
            key: "divider4",
            width: "auto",
            label: <Divider />,
            className: "non-interactive",
          },
          {
            key: "signout",
            icon: <LogoutOutlined />,
            label: "Sign Out",
            onClick: handleSignOut,
          },
        ]
      : []),
  ];

  // Find the active menu item based on the current URL
  const selectedKey =
    items.find((item) => location.pathname.startsWith(item?.path))?.key || "";

  // Disable (but keep visible) the account-gated nav items for profile-only users.
  const menuItems = requiresAccount
    ? items.map((item) =>
        item && ACCOUNT_GATED_MENU_KEYS.has(item.key)
          ? { ...item, disabled: true }
          : item
      )
    : items;

  const darkMenu = (
    <ConfigProvider
      theme={{
        components: {
          Menu: {
            colorBgContainer: "transparent",
            itemColor: "rgba(255,255,255,0.65)",
            itemHoverColor: "#fff",
            itemHoverBg: "rgba(255,255,255,0.09)",
            itemSelectedColor: "#fff",
            itemSelectedBg: hexToRgba(tenant?.theme?.colorPrimary || "#e5791b", 0.25),
            subMenuItemBg: "transparent",
            activeBarBorderWidth: 0,
            colorText: "rgba(255,255,255,0.65)",
            colorTextDisabled: "rgba(255,255,255,0.25)",
          },
        },
      }}
    >
      <Menu
        onClick={handleMenuClick}
        selectedKeys={[selectedKey]}
        mode="inline"
        style={{
          height: "100vh",
          overflow: "auto",
          background: "transparent",
          borderInlineEnd: "none",
        }}
        items={menuItems}
      />
    </ConfigProvider>
  );

  const lightMenu = (
    <Menu
      onClick={handleMenuClick}
      selectedKeys={[selectedKey]}
      mode="inline"
      style={{
        height: "100vh",
        overflow: "auto",
      }}
      items={menuItems}
    />
  );

  return isDarkLayout ? darkMenu : lightMenu;
};

export default Sidebar;

//
//
